"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import classNames from "@/helpers/classNames";
import useMediaQuery from "@/hooks/useMediaQuery";
import Container from "@/components/Container";
import FlagStrip from "@/components/FlagStrip";
import Link from "@/components/Link";
import Text from "@/components/Text";
import Icon from "@/components/Icon";
import SearchButton from "@/components/SearchButton";
import SearchDropdown from "@/components/SearchDropdown";
import CartButton from "@/components/Cart/CartButton";
import { isCurrent } from "@/tools/helpers/link";
import { PORTAL_DASHBOARD_HREF, PORTAL_NAV_LABEL } from "@/tools/portal/constants";
import type { IHeaderObject } from "@/tools/sanity/schema/objects/header";

import styles from "./styles.module.scss";

type NavItemData = IHeaderObject["navItems"][0];

// Every dropdown lives in one shared panel below the header, so moving between
// nav items swaps the contents instead of closing one menu and opening another.
const DROPDOWN_ID = "header-nav-dropdown";

// Leaving the nav doesn't close the panel straight away: crossing the gap
// between two items, or dropping down into the menu itself, would otherwise
// flicker it shut.
const CLOSE_DELAY_MS = 100;

interface HeaderNavigationDesktopProps {
  className?: string;
  header?: IHeaderObject;
  setTheme: (theme: "yellow" | "light" | "dark" | "white") => void;
  featuredSearches?: any;
  isSearchPage?: boolean;
  isSearchActive?: boolean;
  setIsSearchActive?: (value: boolean) => void;
  hasPortalAccess?: boolean;
}

const HeaderNavigationDesktop = ({
  className,
  header,
  setTheme,
  featuredSearches,
  isSearchPage,
  isSearchActive = false,
  setIsSearchActive,
  hasPortalAccess = false,
}: HeaderNavigationDesktopProps) => {
  const { navItems, addButton, button, addSecondaryButton, secondaryButton } = header || {};

  // When signed in, the primary "Login" button becomes a link to the portal.
  // While `NEXT_PUBLIC_DISABLE_PORTAL` is set there is no session to be had —
  // `useSessionUser` answers false without asking — so this falls back to the
  // CMS "Login" entry on its own, with no flag check of its own.
  const primaryLabel = hasPortalAccess ? PORTAL_NAV_LABEL : button?.label;
  const primaryLinkProps = hasPortalAccess ? { href: PORTAL_DASHBOARD_HREF } : button?.link;
  const pathname = usePathname();
  const [openDropdownIndex, setOpenDropdownIndex] = useState<number | null>(null);
  // The panel rendered as the active one. It holds the last opened menu while
  // the dropdown fades out, so its contents don't vanish mid-transition.
  const [visibleDropdownIndex, setVisibleDropdownIndex] = useState<number | null>(null);
  // Mirrors `openDropdownIndex` for handlers that need to read it before the
  // next render — Escape closes and refocuses in the same tick.
  const openDropdownIndexRef = useRef<number | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navRef = useRef<HTMLElement>(null);
  const navItemRefs = useRef(new Map<number, HTMLDivElement>());
  // Bumped to ask the panel to take focus once it has rendered.
  const [panelFocusRequest, setPanelFocusRequest] = useState(0);
  const searchRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery({ down: "smallDesktop" });

  const cancelScheduledClose = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  // A parked panel is `inert`, and an inert subtree can't hold focus — the
  // browser drops it on <body>, which then reads as "focus left the nav" and
  // closes the menu. So focus has to be moved deliberately before a swap.
  const focusIsInDropdown = () => !!document.activeElement?.closest(`#${DROPDOWN_ID}`);

  const returnFocusToTrigger = useCallback((index: number | null) => {
    if (index === null || !focusIsInDropdown()) {
      return;
    }
    navItemRefs.current.get(index)?.querySelector("a")?.focus();
  }, []);

  const openDropdown = useCallback(
    (index: number | null) => {
      cancelScheduledClose();
      // Swapping menus under a focused link carries focus into the new one.
      if (index !== null && index !== openDropdownIndexRef.current && focusIsInDropdown()) {
        setPanelFocusRequest((request) => request + 1);
      }
      openDropdownIndexRef.current = index;
      setOpenDropdownIndex(index);
      if (index !== null) {
        setVisibleDropdownIndex(index);
      }
    },
    [cancelScheduledClose],
  );

  const closeDropdown = useCallback(() => {
    returnFocusToTrigger(openDropdownIndexRef.current);
    openDropdown(null);
  }, [openDropdown, returnFocusToTrigger]);

  const scheduleClose = useCallback(() => {
    cancelScheduledClose();
    closeTimerRef.current = setTimeout(() => {
      returnFocusToTrigger(openDropdownIndexRef.current);
      openDropdownIndexRef.current = null;
      setOpenDropdownIndex(null);
    }, CLOSE_DELAY_MS);
  }, [cancelScheduledClose, returnFocusToTrigger]);

  useEffect(() => cancelScheduledClose, [cancelScheduledClose]);

  // The panel sits after every nav item in the DOM, so tabbing would walk the
  // rest of the nav before reaching it. Opening from the keyboard moves focus
  // straight in instead; Escape hands it back to the trigger. The panel has to
  // do the focusing itself — it can only take focus once the swap is committed.
  const openDropdownWithFocus = useCallback(
    (index: number) => {
      openDropdown(index);
      setPanelFocusRequest((request) => request + 1);
    },
    [openDropdown],
  );

  // Tabbing along the nav moves the open panel with focus, mirroring hover, but
  // never opens it from scratch.
  const handleNavItemFocus = useCallback(
    (index: number, hasDropdown: boolean) => {
      if (openDropdownIndexRef.current === null) {
        return;
      }
      openDropdown(hasDropdown ? index : null);
    },
    [openDropdown],
  );

  const focusNavItem = (index: number) =>
    navItemRefs.current.get(index)?.querySelector("a")?.focus();

  // The panel is a sibling of the nav items, so tabbing off the end of a menu
  // would land on the header buttons. Send it to the next nav item instead, and
  // back to the owning item on shift-tab, so the menu reads as though it were
  // nested under the item that opened it.
  const handlePanelEdgeTab = (event: React.KeyboardEvent<HTMLElement>, index: number) => {
    if (!focusIsInDropdown()) {
      return;
    }
    const links = [
      ...document.querySelectorAll<HTMLAnchorElement>(`#${DROPDOWN_ID} [data-position="active"] a`),
    ];
    if (!links.length) {
      return;
    }

    if (event.shiftKey && document.activeElement === links[0]) {
      event.preventDefault();
      focusNavItem(index);
      return;
    }
    if (!event.shiftKey && document.activeElement === links.at(-1)) {
      event.preventDefault();
      if (navItems && index + 1 < navItems.length) {
        focusNavItem(index + 1);
      } else {
        searchRef.current?.querySelector("button")?.focus();
      }
    }
  };

  const handleNavKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    const index = openDropdownIndexRef.current;
    if (index === null) {
      return;
    }
    if (event.key === "Tab") {
      handlePanelEdgeTab(event, index);
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      closeDropdown();
      focusNavItem(index);
    }
  };

  const handleNavBlur = () => {
    requestAnimationFrame(() => {
      if (!navRef.current?.contains(document.activeElement)) {
        closeDropdown();
      }
    });
  };

  useEffect(() => {
    if (!isSearchActive || isMobile) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsSearchActive?.(false);
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsSearchActive?.(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscapeKey);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [isSearchActive, setIsSearchActive]);

  useEffect(() => {
    // Don't override theme if on search page
    if (isSearchPage) {
      setTheme("white");
    } else if (isSearchActive) {
      setTheme("white");
    } else {
      const headerTheme = openDropdownIndex === null ? "yellow" : "white";
      setTheme(headerTheme);
    }
  }, [setTheme, openDropdownIndex, isSearchActive, isSearchPage]);

  return (
    <nav
      ref={navRef}
      aria-label="Main"
      className={classNames(styles.navigation, className)}
      onKeyDown={handleNavKeyDown}
      onBlur={handleNavBlur}
      onMouseLeave={scheduleClose}
    >
      {navItems?.map((navItem, index) => (
        <NavItem
          key={index}
          index={index}
          navItem={navItem}
          pathname={pathname}
          isOpen={openDropdownIndex === index}
          onOpen={openDropdown}
          onKeyboardOpen={openDropdownWithFocus}
          onFocus={handleNavItemFocus}
          registerRef={(node) => {
            if (node) {
              navItemRefs.current.set(index, node);
            } else {
              navItemRefs.current.delete(index);
            }
          }}
        />
      ))}

      <NavDropdown
        navItems={navItems}
        pathname={pathname}
        isOpen={openDropdownIndex !== null}
        visibleIndex={visibleDropdownIndex}
        focusRequest={panelFocusRequest}
        onMouseEnter={cancelScheduledClose}
        onMouseLeave={scheduleClose}
      />

      <div className={styles.buttons} onMouseEnter={closeDropdown}>
        <div
          ref={searchRef}
          className={classNames(styles.searchNavItem, { [styles.isOpen]: isSearchActive })}
        >
          <SearchButton
            className={styles.searchButton}
            isActive={isSearchActive}
            onClick={() => setIsSearchActive?.(!isSearchActive)}
          />
          <SearchDropdown
            isOpen={isSearchActive}
            featuredSearches={featuredSearches}
            onClose={() => setIsSearchActive?.(false)}
          />
        </div>

        {process.env.NEXT_PUBLIC_ENABLE_CART === "true" && (
          <CartButton className={styles.cartButton} />
        )}

        <div className={styles.divider} />

        {addButton && (
          <Link
            {...primaryLinkProps}
            className={classNames(styles.button, styles.primaryButton)}
            title={primaryLabel}
            aria-label={hasPortalAccess ? PORTAL_NAV_LABEL : undefined}
            variant="content"
            size="md"
            onMouseEnter={() => setTheme("white")}
            onMouseLeave={() => setTheme("yellow")}
          >
            {hasPortalAccess ? (
              <Icon title="user" className={styles.userIcon} color="themeFgDefault" />
            ) : (
              <Text
                className={styles.primaryButtonText}
                text={primaryLabel}
                weight="light"
                color="themeFgDefault"
              />
            )}
          </Link>
        )}
        {addSecondaryButton && (
          <Link
            {...secondaryButton?.link}
            className={classNames(styles.button, styles.secondaryButton)}
            title={secondaryButton?.label}
            size="md"
          >
            <Text
              text={secondaryButton?.label}
              weight="medium"
              alignment="center"
              color="flagWhite"
            />
          </Link>
        )}
      </div>
    </nav>
  );
};

interface NavItemProps {
  navItem: NavItemData;
  pathname: string;
  index: number;
  isOpen: boolean;
  onOpen: (index: number | null) => void;
  onKeyboardOpen: (index: number) => void;
  onFocus: (index: number, hasDropdown: boolean) => void;
  registerRef: (node: HTMLDivElement | null) => void;
}

const NavItem = ({
  navItem,
  pathname,
  index,
  isOpen,
  onOpen,
  onKeyboardOpen,
  onFocus,
  registerRef,
}: NavItemProps) => {
  const isActive =
    isCurrent(pathname, navItem?.link) ||
    (navItem.dropdown &&
      navItem.navSublinks?.some((sublinkGroup) =>
        sublinkGroup.links?.some((sublink) => isCurrent(pathname, sublink?.link)),
      ));
  const hasDropdown = !!navItem.dropdown;

  const handleKeyDown = (event: React.KeyboardEvent<HTMLAnchorElement>) => {
    if (!hasDropdown) {
      return;
    }
    // Both step into the menu — Escape is what closes it. Toggling on Enter
    // would slam the menu shut when focus had already opened it on the way
    // along the nav.
    if (event.key === "Enter" || event.key === "ArrowDown") {
      event.preventDefault();
      onKeyboardOpen(index);
    }
  };

  return (
    <div
      className={classNames(styles.navItem, { [styles.isOpen]: isOpen })}
      ref={registerRef}
      // An item without a dropdown closes the panel rather than opening an
      // empty one.
      onMouseEnter={() => onOpen(hasDropdown ? index : null)}
    >
      <Link
        {...navItem.link}
        className={styles.navLink}
        tabIndex={0}
        variant="content"
        onKeyDown={handleKeyDown}
        onFocus={() => onFocus(index, hasDropdown)}
        forceLinkWhenEmpty
        title={navItem.title}
        aria-haspopup={hasDropdown ? "true" : undefined}
        aria-expanded={hasDropdown ? isOpen : undefined}
        aria-controls={hasDropdown ? DROPDOWN_ID : undefined}
      >
        <span className={classNames(styles.navLinkTitle, { [styles.current]: isActive })}>
          <Text text={navItem.title} weight="light" alignment="center" />
        </span>
      </Link>
    </div>
  );
};

interface NavDropdownProps {
  navItems?: IHeaderObject["navItems"];
  pathname: string;
  isOpen: boolean;
  visibleIndex: number | null;
  focusRequest: number;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

const NavDropdown = ({
  navItems,
  pathname,
  isOpen,
  visibleIndex,
  focusRequest,
  onMouseEnter,
  onMouseLeave,
}: NavDropdownProps) => {
  const panelRefs = useRef(new Map<number, HTMLDivElement>());
  const [panelHeight, setPanelHeight] = useState<number>();
  // Transitions only run while the panel is on screen. Opening from closed
  // snaps the height and the slide positions into place instead, so the menu
  // doesn't resize or slide as it fades in.
  const [isAnimated, setIsAnimated] = useState(false);

  const dropdownItems =
    navItems
      ?.map((navItem, index) => ({ navItem, index }))
      .filter(({ navItem }) => navItem.dropdown) ?? [];

  // The container morphs to the height of whichever menu is showing, so a short
  // menu doesn't leave a tall empty panel behind.
  useEffect(() => {
    const panel = visibleIndex === null ? undefined : panelRefs.current.get(visibleIndex);
    if (!panel) {
      return;
    }

    const measure = () => setPanelHeight(panel.offsetHeight);
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(panel);
    return () => observer.disconnect();
  }, [visibleIndex]);

  // Only a fresh request moves focus — `visibleIndex` also changes on hover,
  // which must never steal it.
  const handledFocusRequest = useRef(focusRequest);
  useEffect(() => {
    if (focusRequest === handledFocusRequest.current || visibleIndex === null) {
      return;
    }
    handledFocusRequest.current = focusRequest;
    panelRefs.current.get(visibleIndex)?.querySelector("a")?.focus();
  }, [focusRequest, visibleIndex]);

  useEffect(() => {
    if (!isOpen) {
      setIsAnimated(false);
      return;
    }
    const frame = requestAnimationFrame(() => setIsAnimated(true));
    return () => cancelAnimationFrame(frame);
  }, [isOpen]);

  if (dropdownItems.length === 0) {
    return null;
  }

  return (
    <div
      id={DROPDOWN_ID}
      data-theme="light"
      className={classNames(styles.wrapper, { [styles.isOpen]: isOpen })}
      aria-hidden={!isOpen}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div
        className={classNames(styles.dropdown, { [styles.isAnimated]: isAnimated })}
        style={{ height: panelHeight }}
      >
        {dropdownItems.map(({ navItem, index }) => (
          <DropdownPanel
            key={index}
            navItem={navItem}
            pathname={pathname}
            isFocusable={isOpen && index === visibleIndex}
            position={getPanelPosition(index, visibleIndex)}
            registerRef={(node) => {
              if (node) {
                panelRefs.current.set(index, node);
              } else {
                panelRefs.current.delete(index);
              }
            }}
          />
        ))}
      </div>

      <FlagStrip className={styles.flags} variant="outline" tiled />
    </div>
  );
};

// Menus to the left of the visible one park off to the left and vice versa, so
// the contents always slide in from the side the pointer came from.
const getPanelPosition = (index: number, visibleIndex: number | null) => {
  if (visibleIndex === null) {
    return "after";
  }
  if (index === visibleIndex) {
    return "active";
  }
  return index < visibleIndex ? "before" : "after";
};

interface DropdownPanelProps {
  navItem: NavItemData;
  pathname: string;
  isFocusable: boolean;
  position: "before" | "active" | "after";
  registerRef: (node: HTMLDivElement | null) => void;
}

const DropdownPanel = ({
  navItem,
  pathname,
  isFocusable,
  position,
  registerRef,
}: DropdownPanelProps) => (
  <div ref={registerRef} className={styles.panel} data-position={position} inert={!isFocusable}>
    <Container className={styles.container}>
      {navItem.navSublinks?.map((sublink, index) => (
        <div key={index} className={styles.sublinkGroup}>
          <Text
            className={styles.sublinkGroupTitle}
            text={sublink.title}
            variant="eyebrow"
            color={sublink?.titleColour?.colorKey}
          />

          {sublink?.links?.[0] && (
            <ul className={styles.sublinks}>
              {sublink.links.map((linkItem, linkIndex) => (
                <li key={linkIndex} className={styles.sublinkContainer}>
                  <Link
                    {...linkItem.link}
                    className={classNames(styles.sublink, {
                      [styles.current]: isCurrent(pathname, linkItem?.link),
                    })}
                    tabIndex={isFocusable ? 0 : -1} // Only focusable while this menu is showing
                    // No `title`: `Link` turns it into an `aria-label`, which named the link by its
                    // title alone when it shows a description as well (WCAG 2.5.3).
                    variant="content"
                  >
                    <Text text={linkItem.title} size="md" weight="semibold" />

                    {linkItem?.description && (
                      <Text text={linkItem.description} variant="label" size="md" weight="light" />
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </Container>
  </div>
);

export default HeaderNavigationDesktop;
